import {
  registerOntologyMapping,
  type OntologyTypeMapping,
} from '../../../core/data-profiles/OntologyPropertyMap';

/**
 * GRC catalog ontology mappings — covers every node type the
 * `grc-control` profile owns: Control, Risk, Obligation, Evidence,
 * Finding, ControlFramework, Vendor, Policy, Capability.
 *
 * Each mapping projects the universal-ontology props documented in
 * docs/architecture/universal-ontology.md §"GRC Entity Details" into
 * a runtime node shape. The `runtimeType` is the universal node type
 * itself — GRC records render through generic record panels (the
 * Controls Library / Assurance) rather than dedicated canvas shapes,
 * so there's a single 1:1 mapping per node type.
 *
 * Bootstrapped from the same place process ontology mappings register;
 * see `registerProcessOntologyMappings.ts` for the pattern.
 */

const controlMapping: OntologyTypeMapping = {
  ontologyType: 'control',
  runtimeType: 'control',
  unknownPropertyStrategy: 'attrs',
  properties: [
    { ontologyKey: 'id', runtimePath: 'attrs.id', type: 'string' },
    { ontologyKey: 'controlId', runtimePath: 'attrs.controlId', panelField: 'controlId', type: 'string' },
    { ontologyKey: 'name', runtimePath: 'attrs.name', panelField: 'name', type: 'string' },
    { ontologyKey: 'title', runtimePath: 'attrs.title', panelField: 'title', type: 'string' },
    { ontologyKey: 'description', runtimePath: 'attrs.description', panelField: 'description', type: 'string' },
    { ontologyKey: 'framework', runtimePath: 'attrs.framework', panelField: 'framework', type: 'string' },
    { ontologyKey: 'frameworkRef', runtimePath: 'attrs.frameworkRef', panelField: 'frameworkRef', type: 'string' },
    { ontologyKey: 'controlType', runtimePath: 'attrs.controlType', panelField: 'controlType', type: 'string', description: 'preventive | detective | corrective | directive' },
    { ontologyKey: 'controlNature', runtimePath: 'attrs.controlNature', panelField: 'controlNature', type: 'string', description: 'manual | automated | it-dependent-manual' },
    { ontologyKey: 'frequency', runtimePath: 'attrs.frequency', panelField: 'frequency', type: 'string' },
    { ontologyKey: 'controlObjective', runtimePath: 'attrs.controlObjective', panelField: 'controlObjective', type: 'string' },
    { ontologyKey: 'designEffectiveness', runtimePath: 'attrs.designEffectiveness', panelField: 'designEffectiveness', type: 'string' },
    { ontologyKey: 'operatingEffectiveness', runtimePath: 'attrs.operatingEffectiveness', panelField: 'operatingEffectiveness', type: 'string' },
    { ontologyKey: 'keyControl', runtimePath: 'attrs.keyControl', panelField: 'keyControl', type: 'boolean' },
    { ontologyKey: 'owner', runtimePath: 'attrs.owner', panelField: 'owner', type: 'string' },
    { ontologyKey: 'category', runtimePath: 'attrs.category', panelField: 'category', type: 'string', description: 'Control family' },
    { ontologyKey: 'severity', runtimePath: 'attrs.severity', panelField: 'severity', type: 'string' },
    { ontologyKey: 'complianceStatus', runtimePath: 'attrs.complianceStatus', panelField: 'complianceStatus', type: 'string' },
  ],
};

const riskMapping: OntologyTypeMapping = {
  ontologyType: 'risk',
  runtimeType: 'risk',
  unknownPropertyStrategy: 'attrs',
  properties: [
    { ontologyKey: 'id', runtimePath: 'attrs.id', type: 'string' },
    { ontologyKey: 'name', runtimePath: 'attrs.name', panelField: 'name', type: 'string' },
    { ontologyKey: 'title', runtimePath: 'attrs.title', panelField: 'title', type: 'string' },
    { ontologyKey: 'description', runtimePath: 'attrs.description', panelField: 'description', type: 'string' },
    { ontologyKey: 'likelihood', runtimePath: 'attrs.likelihood', panelField: 'likelihood', type: 'number', description: '1–5 scale' },
    { ontologyKey: 'impact', runtimePath: 'attrs.impact', panelField: 'impact', type: 'number', description: '1–5 scale' },
    { ontologyKey: 'inherentScore', runtimePath: 'attrs.inherentScore', panelField: 'inherentScore', type: 'number' },
    { ontologyKey: 'residualScore', runtimePath: 'attrs.residualScore', panelField: 'residualScore', type: 'number' },
    { ontologyKey: 'riskCategory', runtimePath: 'attrs.riskCategory', panelField: 'riskCategory', type: 'string', description: 'operational | strategic | financial | compliance | technology | reputational' },
    { ontologyKey: 'treatmentType', runtimePath: 'attrs.treatmentType', panelField: 'treatmentType', type: 'string', description: 'mitigate | accept | transfer | avoid' },
    { ontologyKey: 'velocity', runtimePath: 'attrs.velocity', panelField: 'velocity', type: 'string' },
    { ontologyKey: 'riskAppetiteThreshold', runtimePath: 'attrs.riskAppetiteThreshold', panelField: 'riskAppetiteThreshold', type: 'number' },
    { ontologyKey: 'kpiMetric', runtimePath: 'attrs.kpiMetric', panelField: 'kpiMetric', type: 'string' },
    { ontologyKey: 'severity', runtimePath: 'attrs.severity', panelField: 'severity', type: 'string' },
    { ontologyKey: 'owner', runtimePath: 'attrs.owner', panelField: 'owner', type: 'string' },
    { ontologyKey: 'status', runtimePath: 'attrs.status', panelField: 'status', type: 'string' },
  ],
};

const obligationMapping: OntologyTypeMapping = {
  ontologyType: 'obligation',
  runtimeType: 'obligation',
  unknownPropertyStrategy: 'attrs',
  properties: [
    { ontologyKey: 'id', runtimePath: 'attrs.id', type: 'string' },
    { ontologyKey: 'name', runtimePath: 'attrs.name', panelField: 'name', type: 'string' },
    { ontologyKey: 'regulationName', runtimePath: 'attrs.regulationName', panelField: 'regulationName', type: 'string' },
    { ontologyKey: 'articleRef', runtimePath: 'attrs.articleRef', panelField: 'articleRef', type: 'string' },
    { ontologyKey: 'jurisdictions', runtimePath: 'attrs.jurisdictions', panelField: 'jurisdictions', type: 'string[]' },
    { ontologyKey: 'effectiveDate', runtimePath: 'attrs.effectiveDate', panelField: 'effectiveDate', type: 'string' },
    { ontologyKey: 'complianceStatus', runtimePath: 'attrs.complianceStatus', panelField: 'complianceStatus', type: 'string', description: 'compliant | non-compliant | partial | not-assessed' },
    { ontologyKey: 'obligationText', runtimePath: 'attrs.obligationText', panelField: 'obligationText', type: 'string' },
  ],
};

const evidenceMapping: OntologyTypeMapping = {
  ontologyType: 'evidence',
  runtimeType: 'evidence',
  unknownPropertyStrategy: 'attrs',
  properties: [
    { ontologyKey: 'id', runtimePath: 'attrs.id', type: 'string' },
    { ontologyKey: 'name', runtimePath: 'attrs.name', panelField: 'name', type: 'string' },
    { ontologyKey: 'collectionDate', runtimePath: 'attrs.collectionDate', panelField: 'collectionDate', type: 'string' },
    { ontologyKey: 'testPeriod', runtimePath: 'attrs.testPeriod', panelField: 'testPeriod', type: 'object', description: '{ start, end }' },
    { ontologyKey: 'evidenceType', runtimePath: 'attrs.evidenceType', panelField: 'evidenceType', type: 'string', description: 'document | system-report | screenshot | log | attestation | vendor-attestation | automation-log' },
    { ontologyKey: 'sufficiencyRating', runtimePath: 'attrs.sufficiencyRating', panelField: 'sufficiencyRating', type: 'string', description: 'sufficient | insufficient | missing' },
    { ontologyKey: 'retentionDate', runtimePath: 'attrs.retentionDate', panelField: 'retentionDate', type: 'string' },
    { ontologyKey: 'collectionMethod', runtimePath: 'attrs.collectionMethod', panelField: 'collectionMethod', type: 'string', description: 'inquiry | observation | inspection | re-performance' },
    { ontologyKey: 'r2Key', runtimePath: 'attrs.r2Key', type: 'string', description: 'R2 object key when this Evidence has a binary attachment.' },
    { ontologyKey: 'sha256', runtimePath: 'attrs.sha256', type: 'string' },
    { ontologyKey: 'mimeType', runtimePath: 'attrs.mimeType', type: 'string' },
    { ontologyKey: 'byteSize', runtimePath: 'attrs.byteSize', type: 'number' },
  ],
};

const findingMapping: OntologyTypeMapping = {
  ontologyType: 'finding',
  runtimeType: 'finding',
  unknownPropertyStrategy: 'attrs',
  properties: [
    { ontologyKey: 'id', runtimePath: 'attrs.id', type: 'string' },
    { ontologyKey: 'name', runtimePath: 'attrs.name', panelField: 'name', type: 'string' },
    { ontologyKey: 'severity', runtimePath: 'attrs.severity', panelField: 'severity', type: 'string', description: 'critical | high | medium | low' },
    { ontologyKey: 'findingType', runtimePath: 'attrs.findingType', panelField: 'findingType', type: 'string', description: 'design-deficiency | operating-deficiency | observation' },
    { ontologyKey: 'status', runtimePath: 'attrs.status', panelField: 'status', type: 'string', description: 'open | remediation-in-progress | closed | accepted' },
    { ontologyKey: 'dueDate', runtimePath: 'attrs.dueDate', panelField: 'dueDate', type: 'string' },
    { ontologyKey: 'rootCause', runtimePath: 'attrs.rootCause', panelField: 'rootCause', type: 'string' },
    { ontologyKey: 'remediationPlan', runtimePath: 'attrs.remediationPlan', panelField: 'remediationPlan', type: 'string' },
    { ontologyKey: 'externalTicketUrl', runtimePath: 'attrs.externalTicketUrl', panelField: 'externalTicketUrl', type: 'string' },
    { ontologyKey: 'owner', runtimePath: 'attrs.owner', panelField: 'owner', type: 'string' },
  ],
};

const controlFrameworkMapping: OntologyTypeMapping = {
  ontologyType: 'control-framework',
  runtimeType: 'control-framework',
  unknownPropertyStrategy: 'attrs',
  properties: [
    { ontologyKey: 'id', runtimePath: 'attrs.id', type: 'string' },
    { ontologyKey: 'name', runtimePath: 'attrs.name', panelField: 'name', type: 'string' },
    { ontologyKey: 'frameworkName', runtimePath: 'attrs.frameworkName', panelField: 'frameworkName', type: 'string' },
    { ontologyKey: 'level', runtimePath: 'attrs.level', panelField: 'level', type: 'string', description: 'framework | function | category | subcategory | control | test-procedure' },
    { ontologyKey: 'parentRef', runtimePath: 'attrs.parentRef', panelField: 'parentRef', type: 'string' },
    { ontologyKey: 'referenceId', runtimePath: 'attrs.referenceId', panelField: 'referenceId', type: 'string' },
    { ontologyKey: 'version', runtimePath: 'attrs.version', panelField: 'version', type: 'string' },
    { ontologyKey: 'description', runtimePath: 'attrs.description', panelField: 'description', type: 'string' },
  ],
};

const vendorMapping: OntologyTypeMapping = {
  ontologyType: 'vendor',
  runtimeType: 'vendor',
  unknownPropertyStrategy: 'attrs',
  properties: [
    { ontologyKey: 'id', runtimePath: 'attrs.id', type: 'string' },
    { ontologyKey: 'name', runtimePath: 'attrs.name', panelField: 'name', type: 'string' },
    { ontologyKey: 'riskTier', runtimePath: 'attrs.riskTier', panelField: 'riskTier', type: 'string', description: 'critical | high | medium | low' },
    { ontologyKey: 'contractExpiry', runtimePath: 'attrs.contractExpiry', panelField: 'contractExpiry', type: 'string' },
    { ontologyKey: 'lastAssessmentDate', runtimePath: 'attrs.lastAssessmentDate', panelField: 'lastAssessmentDate', type: 'string' },
    { ontologyKey: 'serviceCategories', runtimePath: 'attrs.serviceCategories', panelField: 'serviceCategories', type: 'string[]' },
    { ontologyKey: 'jurisdictions', runtimePath: 'attrs.jurisdictions', panelField: 'jurisdictions', type: 'string[]' },
    { ontologyKey: 'subcontractors', runtimePath: 'attrs.subcontractors', panelField: 'subcontractors', type: 'string[]' },
  ],
};

const policyMapping: OntologyTypeMapping = {
  ontologyType: 'policy',
  runtimeType: 'policy',
  unknownPropertyStrategy: 'attrs',
  properties: [
    { ontologyKey: 'id', runtimePath: 'attrs.id', type: 'string' },
    { ontologyKey: 'name', runtimePath: 'attrs.name', panelField: 'name', type: 'string' },
    { ontologyKey: 'definition', runtimePath: 'attrs.definition', panelField: 'definition', type: 'string' },
    { ontologyKey: 'governsProcess', runtimePath: 'attrs.governsProcess', panelField: 'governsProcess', type: 'string' },
    { ontologyKey: 'description', runtimePath: 'attrs.description', panelField: 'description', type: 'string' },
  ],
};

const capabilityMapping: OntologyTypeMapping = {
  ontologyType: 'capability',
  runtimeType: 'capability',
  unknownPropertyStrategy: 'attrs',
  properties: [
    { ontologyKey: 'id', runtimePath: 'attrs.id', type: 'string' },
    { ontologyKey: 'name', runtimePath: 'attrs.name', panelField: 'name', type: 'string' },
    { ontologyKey: 'decompositionLevel', runtimePath: 'attrs.decompositionLevel', panelField: 'decompositionLevel', type: 'string', description: 'L0 | L1 | L2 | L3 | L4' },
    { ontologyKey: 'maturityLevel', runtimePath: 'attrs.maturityLevel', panelField: 'maturityLevel', type: 'number' },
    { ontologyKey: 'strategicImportance', runtimePath: 'attrs.strategicImportance', panelField: 'strategicImportance', type: 'string' },
    { ontologyKey: 'investmentLevel', runtimePath: 'attrs.investmentLevel', panelField: 'investmentLevel', type: 'string' },
    { ontologyKey: 'owner', runtimePath: 'attrs.owner', panelField: 'owner', type: 'string' },
    { ontologyKey: 'description', runtimePath: 'attrs.description', panelField: 'description', type: 'string' },
  ],
};

let registered = false;

export function registerGrcCatalogOntologyMappings(): void {
  if (registered) return;
  registerOntologyMapping(controlMapping);
  registerOntologyMapping(riskMapping);
  registerOntologyMapping(obligationMapping);
  registerOntologyMapping(evidenceMapping);
  registerOntologyMapping(findingMapping);
  registerOntologyMapping(controlFrameworkMapping);
  registerOntologyMapping(vendorMapping);
  registerOntologyMapping(policyMapping);
  registerOntologyMapping(capabilityMapping);
  registered = true;
}
